import { useState } from 'react';
import { Button, Divider, Modal, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import Icon from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { ReactComponent as StripePayIcon } from 'assets/pay/stripe.svg';
import { subscriptionCancel } from 'services/api/pay';
import { refreshComboInfo, useComboInfo } from '../hooks/useComboInfo';
import { refreshBotUsageSummary } from 'hooks/business/system/useBot';
import { useOrgValue } from 'hooks/business/system/useOrg';
import { useQueryClient } from '@tanstack/react-query';

export interface ManageSubscriptionModalProps {
  visible: boolean;
  /** 生效中的订阅套餐 */
  orderedPackage?: Pay.OrderedPackage;
  onCancel: () => void;
}

/**
 * 管理自动续费弹窗：
 * - 主弹窗展示订阅信息，可点击「关闭」进入确认弹窗
 * - 确认弹窗确认后调用取消订阅接口，并刷新套餐状态
 */
function ManageSubscriptionModal(props: ManageSubscriptionModalProps) {
  const { visible, orderedPackage, onCancel } = props;
  const { t } = useTranslate();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const orgValue = useOrgValue();
  const queryClient = useQueryClient();

  const packages = useComboInfo();

  const { combo } = orgValue || ({} as Oversea.Org);
  const comboNameTmp = t({
    key: `packageName.${combo}`,
    defaultValue: combo,
  });
  const workingPack = packages.find((_) => _.autoRenewing);

  const comboName = `${comboNameTmp}(${t(`套餐.${workingPack?.id}`)})`;
  const endDate = orderedPackage?.validTimeEnd;

  const handleConfirmClose = () => {
    setCanceling(true);
    subscriptionCancel()
      .then(() => {
        setConfirmVisible(false);
        onCancel();
        // 取消后订阅进入 CANCELING，刷新套餐状态使 autoRenewing 变为 false
        refreshComboInfo();
        refreshBotUsageSummary();
        // 顶部 Profile 的「月付/自动续费」标签来自 orderedPackage query，一并失效
        queryClient.invalidateQueries({ queryKey: ['accounts', 'orderedPackage'] });
      })
      .catch(() => {
        message.error(t('translation.操作失败，请稍后再试'));
      })
      .finally(() => setCanceling(false));
  };

  return (
    <>
      <Modal
        visible={visible}
        title={t('管理自动续费')}
        width={480}
        footer={null}
        centered
        onCancel={onCancel}
      >
        <Card>
          <div className="flex items-center">
            <Name>{comboName}</Name>
            <MonthTag>{t('月付')}</MonthTag>
          </div>
          <Row>
            {t('自动续费')}：{t('phrase.已开启')}
          </Row>
          <Row>
            {t('下次自动续费时间')}：{endDate}
          </Row>
          <Divider />
          <PayRow>
            <PayChannel>
              <Icon component={StripePayIcon} />
              <span>Stripe</span>
            </PayChannel>
            <CloseBtn type="text" onClick={() => setConfirmVisible(true)}>
              {t('marketing.关闭')}
            </CloseBtn>
          </PayRow>
        </Card>
      </Modal>

      <Modal
        visible={confirmVisible}
        width={400}
        closable={false}
        centered
        footer={
          <Footer>
            <CancelBtn onClick={() => setConfirmVisible(false)}>{t('取消')}</CancelBtn>
            <ConfirmBtn type="primary" loading={canceling} onClick={handleConfirmClose}>
              {t('确认关闭')}
            </ConfirmBtn>
          </Footer>
        }
      >
        <ConfirmContent>
          <ExclamationCircleFilled
            style={{ color: 'var(--fett-surface-warning-text)', fontSize: 20 }}
          />
          <div className="ml-2 flex-1">
            <Title>{t('确认要关闭自动续费吗？')}</Title>
            <Desc>{t('确认要关闭自动续费吗tips', { endDate })}</Desc>
          </div>
        </ConfirmContent>
      </Modal>
    </>
  );
}

export default ManageSubscriptionModal;

const Card = styled.div`
  background: var(--fett-bg-subtle);
  padding: 16px;
  border-radius: 8px;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--fett-text-primary);
`;

const MonthTag = styled.span`
  margin-left: 6px;
  height: 18px;
  line-height: 18px;
  padding: 0 4px;
  border-radius: 2px;
  background: var(--fett-bg-info-tip);
  color: var(--fett-scenario-accent-text);
  font-size: 12px;
`;

const Row = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: var(--fett-text-tertiary);
`;

const PayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const PayChannel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--fett-text-secondary);
  svg {
    width: 18px;
    height: 18px;
  }
`;

const CloseBtn = styled(Button)`
  color: var(--fett-text-tertiary);
  &:hover {
    color: var(--fett-surface-error-text) !important;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const CancelBtn = styled(Button)`
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-input);
  color: var(--fett-text-secondary);
`;

const ConfirmBtn = styled(Button)`
  background: var(--fett-app-primary-color);
  border-color: var(--fett-app-primary-color);
`;

const ConfirmContent = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--fett-text-primary);
`;

const Desc = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: var(--fett-text-tertiary);
  line-height: 22px;
`;
